import { Dispatch, SetStateAction } from 'react';

import { useDefaultStatusFilter } from '@/hooks/useDefaultStatusFilter';
import { SeedFilter } from '@/hooks/useSeeds';
import { SeedType } from '@/sections/seeds/types';
import { omit } from '@/utils/lodash.util';
import { getCurrentTenantId } from '@/utils/storage/tenantScoping.util';

export const applySeedActiveFilter = (prev: SeedFilter): SeedFilter => ({
  ...prev,
  advancedFilters: {
    ...prev.advancedFilters,
    status: ['A'],
  },
});

export const clearSeedActiveFilter = (prev: SeedFilter): SeedFilter => {
  const restAdvanced = omit(prev.advancedFilters, 'status');
  return {
    ...prev,
    advancedFilters:
      Object.keys(restAdvanced).length > 0 ? restAdvanced : undefined,
  };
};

/**
 * Per-tab default "Approved" filter for the Seeds page (ENG-4982).
 *
 * One sessionStorage gate per (tenant × seedType): each of the four tabs
 * gets status ['A'] applied the first time it is viewed in a browser
 * session, and silently auto-clears (the generic hook's provenance path)
 * when the tab has zero approved rows. The key keeps the
 * `:seed-default-active-filter` suffix so the logout sweep in
 * state/auth.tsx matches it unchanged.
 *
 * `enabled` gates on `filters.type === seedType`: after a tab switch,
 * `filters` belongs to the OUTGOING tab until useSeedFilters' type-change
 * reset lands (one commit later). Gating off in that window (a) prevents
 * the apply from landing on the outgoing tab's state and (b) prevents the
 * outgoing tab's stale isFilteredEmpty from auto-clearing the incoming
 * tab's filter. On cached-fresh revisits (success, not fetching) the
 * cross-tab safety net is the provenance compare itself: every applied
 * filter object embeds `type`, so isEqual against another tab's state is
 * structurally false.
 *
 * The generic hook tracks applied-filter provenance per sessionKey, so
 * each tab's late auto-clear survives tab interleaving: a zero-approved
 * tab abandoned before its first result lands still auto-clears when it
 * is revisited and resolves empty.
 */
export function useSeedDefaultStatusFilter({
  seedType,
  filters,
  setFilters,
  isFilteredEmpty,
  dataStatus,
  isFetching,
  enabled = true,
}: {
  seedType: SeedType;
  filters: SeedFilter;
  setFilters: Dispatch<SetStateAction<SeedFilter>>;
  isFilteredEmpty: boolean;
  dataStatus: 'pending' | 'error' | 'success';
  isFetching: boolean;
  enabled?: boolean;
}) {
  useDefaultStatusFilter<SeedFilter>({
    sessionKey: `${getCurrentTenantId()}:${seedType}:seed-default-active-filter`,
    applyFilter: applySeedActiveFilter,
    clearFilter: clearSeedActiveFilter,
    setFilters,
    filters,
    isFilteredEmpty,
    dataStatus,
    isFetching,
    enabled: enabled && filters.type === seedType,
  });
}
